/** @jsxImportSource @emotion/react */

import { useLocation, useNavigate } from 'react-router-dom';
import * as s from './style';
import React, { useEffect, useState, useRef } from 'react';
import { AppBar, Toolbar } from '@mui/material';
import {
    Mail,
    Instagram,
    Science,
    Map,
    LocationOn,
    CollectionsBookmark,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Login,
    Logout,
    AccountCircle,
    Insights,
} from '@mui/icons-material';
import CommonButton from '../atoms/button/CommonButton';
import { useRecoilState } from 'recoil';
import { logTokenAtom } from '../../atoms/log/LogTokenStore';
import { createLog } from '../../atoms/log/LogFunction';
import { useAuth } from 'context/AuthContext';

const Header = ({ isRoot }) => {
    const location = useLocation();
    const [logTokenState, setLogTokenState] = useRecoilState(logTokenAtom);

    const navigate = useNavigate();
    const [logoSrc, setLogoSrc] = useState('/images/sodamap_logo1.png');
    const [isLab1DropdownOpen, setIsLab1DropdownOpen] = useState(false);
    const [isLab2DropdownOpen, setIsLab2DropdownOpen] = useState(false);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [isMyMapDropdownOpen, setIsMyMapDropdownOpen] = useState(false);
    const { isLoggedIn, logout, loggedInUser } = useAuth();
    const lab1DropdownRef = useRef(null);
    const lab2DropdownRef = useRef(null);
    const userDropdownRef = useRef(null);
    const myMapDropdownRef = useRef(null);

    useEffect(() => {
        if (isRoot) {
            setLogoSrc('/images/sodamap_logo1.png');
        } else {
            setLogoSrc('/images/sodamap_logo2.png');
        }
    }, [isRoot]);

    // 외부 클릭 시 드롭다운 닫기
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (lab1DropdownRef.current && !lab1DropdownRef.current.contains(event.target)) {
                setIsLab1DropdownOpen(false);
            }
            if (lab2DropdownRef.current && !lab2DropdownRef.current.contains(event.target)) {
                setIsLab2DropdownOpen(false);
            }
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
                setIsUserDropdownOpen(false);
            }
            if (myMapDropdownRef.current && !myMapDropdownRef.current.contains(event.target)) {
                setIsMyMapDropdownOpen(false);
            }
        };

        if (isLab1DropdownOpen || isLab2DropdownOpen || isUserDropdownOpen || isMyMapDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isLab1DropdownOpen, isLab2DropdownOpen, isUserDropdownOpen, isMyMapDropdownOpen]);

    const onClickLogoHandle = async () => {
        navigate('/');

        const logOption = {
            page: location.pathname,
            eventName: 'navigate_home',
            eventValue: null,
            sessionToken: logTokenState,
        };

        const newToken = await createLog(logOption);
        setLogTokenState(newToken);
    };

    const onClickMailButtonHandle = async () => {
        window.open('mailto:info@ypt.kr;info@plqpartners.com', '_blank');

        const logOption = {
            page: location.pathname,
            eventName: 'open_mail',
            eventValue: null,
            sessionToken: logTokenState,
        };

        const newToken = await createLog(logOption);
        setLogTokenState(newToken);
    };

    const onClickInstagramButtonHandle = async () => {
        window.open('https://www.instagram.com/y_partners_/?utm_medium=copy_link', '_blank');

        const logOption = {
            page: location.pathname,
            eventName: 'open_instagram',
            eventValue: null,
            sessionToken: logTokenState,
        };

        const newToken = await createLog(logOption);
        setLogTokenState(newToken);
    };

    const onClickGalleryButtonHandle = async () => {
        navigate('/gallery', { replace: true });

        const logOption = {
            page: location.pathname,
            eventName: 'navigate_gallery',
            eventValue: null,
            sessionToken: logTokenState,
        };

        const newToken = await createLog(logOption);
        setLogTokenState(newToken);
    };

    const onClickSodamapButtonHandle = async () => {
        navigate('/sodamap', { replace: true });

        const logOption = {
            page: location.pathname,
            eventName: 'navigate_sodamap',
            eventValue: null,
            sessionToken: logTokenState,
        };

        const newToken = await createLog(logOption);
        setLogTokenState(newToken);
    };

    const onClickLab2ButtonHandle = () => {
        setIsLab2DropdownOpen(!isLab2DropdownOpen);
    };

    const onClickLab1ButtonHandle = () => {
        setIsLab1DropdownOpen(!isLab1DropdownOpen);
    };

    const onClickLab1SubmenuItem = (path) => {
        navigate(path, { replace: true });
        setIsLab1DropdownOpen(false);
    };

    const onClickLab2SubmenuItem = (path) => {
        navigate(path, { replace: true });
        setIsLab2DropdownOpen(false);
    };

    const onClickUserDropdownHandle = () => {
        setIsUserDropdownOpen(!isUserDropdownOpen);
    };

    const onClickLogoutHandle = async () => {
        setIsUserDropdownOpen(false);
        logout();
    };

    const onClickLoginButtonHandle = async () => {
        navigate('/user/login',  {state: {from: location.pathname}});
    };

    const onClickMyMapButtonHandle = () => {
        setIsMyMapDropdownOpen(!isMyMapDropdownOpen);
    };

    const onClickMyMapSubmenuItem = (path) => {
        navigate(path, { replace: true });
        setIsMyMapDropdownOpen(false);
    };

    return (
        <AppBar css={s.mainContainer(isRoot)} position="static" elevation={0}>
            <Toolbar css={s.toolbar(isRoot)}>
                {/* 로고 - 왼쪽 */}
                <div css={s.logo}>
                    <div css={s.logoClickArea} onClick={onClickLogoHandle}>
                        <img css={s.logoImg} src={logoSrc} alt="logo" />
                        <span css={s.subText(isRoot)}>Social Data Map</span>
                    </div>
                </div>

                {/* 네비게이션 버튼들 - 가운데 */}
                <div css={s.navigationContainer}>
                    {!isRoot && (
                        <>
                            <div css={s.dropdownContainer} ref={lab1DropdownRef}>
                                <CommonButton
                                    variant="navigation"
                                    navigationType="lab1"
                                    size="small"
                                    layout="horizontal"
                                    // startIcon={<Science />}
                                    startIcon={<Insights />}
                                    endIcon={isLab1DropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    onClick={onClickLab1ButtonHandle}
                                    customStyles={s.navigationButton(isRoot)}
                                >
                                    통계 비교·분석
                                </CommonButton>
                                {isLab1DropdownOpen && (
                                    <div css={s.dropdownMenu(isRoot)}>
                                        <div
                                            css={s.dropdownItem(isRoot)}
                                            onClick={() => onClickLab1SubmenuItem('/lab/lab_1/mode_select')}
                                        >
                                            통계 비교
                                        </div>
                                        <div
                                            css={s.dropdownItem(isRoot)}
                                            onClick={() => onClickLab1SubmenuItem('/lab/lab_1/point_data')}
                                        >
                                            통계 분석
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div css={s.dropdownContainer} ref={myMapDropdownRef}>
                                <CommonButton
                                    variant="navigation"
                                    navigationType="address"
                                    size="small"
                                    layout="horizontal"
                                    startIcon={<Map />}
                                    endIcon={isMyMapDropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    onClick={onClickMyMapButtonHandle}
                                    customStyles={s.navigationButton(isRoot)}
                                >
                                    내 지도 만들기
                                </CommonButton>
                                {isMyMapDropdownOpen && (
                                    <div css={s.dropdownMenu(isRoot)}>
                                        <div
                                            css={s.dropdownItem(isRoot)}
                                            onClick={() => onClickMyMapSubmenuItem('/my_map/geocoder')}
                                        >
                                            주소 찾기
                                        </div>
                                        <div
                                            css={s.dropdownItem(isRoot)}
                                            // onClick={() => onClickMyMapSubmenuItem('/lab/lab_2/user_data')}
                                            onClick={() => onClickMyMapSubmenuItem('/my_map/user_data')}
                                        >
                                            사용자 데이터
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div css={s.dropdownContainer} ref={lab2DropdownRef}>
                                <CommonButton
                                    variant="navigation"
                                    navigationType="lab2"
                                    size="small"
                                    layout="horizontal"
                                    startIcon={<Science />}
                                    endIcon={isLab2DropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    onClick={onClickLab2ButtonHandle}
                                    customStyles={s.navigationButton(isRoot)}
                                >
                                    실험실
                                </CommonButton>
                                {isLab2DropdownOpen && (
                                    <div css={s.dropdownMenu(isRoot)}>
                                        <div
                                            css={s.dropdownItem(isRoot)}
                                            onClick={() => onClickLab2SubmenuItem('/lab/lab_2/maps/population')}
                                        >
                                            인구 정보
                                        </div>
                                        <div
                                            css={s.dropdownItem(isRoot)}
                                            onClick={() => onClickLab2SubmenuItem('/lab/lab_2/maps/housing')}
                                        >
                                            주택 정보
                                        </div>
                                        <div
                                            css={s.dropdownItem(isRoot)}
                                            onClick={() => onClickLab2SubmenuItem('/lab/lab_2/maps/lifestyle')}
                                        >
                                            생활 정보
                                        </div>
                                        <div
                                            css={s.dropdownItem(isRoot)}
                                            onClick={() => window.open("https://sodamap.co.kr:8448", '_blank')}
                                        >
                                            AI방역 플랫폼
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* <CommonButton
                                variant="navigation"
                                navigationType="address"
                                size="small"
                                layout="horizontal"
                                startIcon={<Map />}
                                onClick={() => window.open('https://sodamap.co.kr:8449/', '_blank')}
                                customStyles={s.navigationButton(isRoot)}
                            >
                                주소찾기
                            </CommonButton> */}

                            <CommonButton
                                variant="navigation"
                                navigationType="gallery"
                                size="small"
                                layout="horizontal"
                                startIcon={<CollectionsBookmark />}
                                onClick={onClickGalleryButtonHandle}
                                customStyles={s.navigationButton(isRoot)}
                            >
                                Gallery
                            </CommonButton>
                            <CommonButton
                                variant="navigation"
                                navigationType="map"
                                size="small"
                                layout="horizontal"
                                startIcon={<LocationOn />}
                                onClick={onClickSodamapButtonHandle}
                                customStyles={s.navigationButton(isRoot)}
                            >
                                Use Map
                            </CommonButton>
                        </>
                    )}
                </div>

                {/* 소셜 링크 - 오른쪽 */}
                <div css={s.socialLinksContainer}>
                    {isLoggedIn ? (
                        <div css={s.dropdownContainer} ref={userDropdownRef}>
                            <CommonButton
                                variant="navigation"
                                size="small"
                                layout="horizontal"
                                startIcon={<AccountCircle />}
                                endIcon={isUserDropdownOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                onClick={onClickUserDropdownHandle}
                                customStyles={s.userButton(isRoot)}
                            >
                                {loggedInUser}
                            </CommonButton>
                            {isUserDropdownOpen && (
                                <div css={s.dropdownMenu(isRoot)}>
                                    <div css={s.dropdownItem(isRoot)} onClick={onClickLogoutHandle}>
                                        <Logout style={{ marginRight: '8px', fontSize: '18px' }} />
                                        로그아웃
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <CommonButton
                            variant="outlined"
                            size="small"
                            layout="horizontal"
                            startIcon={<Login />}
                            onClick={onClickLoginButtonHandle}
                            customStyles={s.loginButton(isRoot)}
                        >
                            로그인
                        </CommonButton>
                    )}
                    <CommonButton
                        variant="image"
                        size="icon"
                        layout="horizontal"
                        onClick={onClickMailButtonHandle}
                        customStyles={s.mailButton(isRoot)}
                    >
                        <Mail />
                    </CommonButton>
                    <CommonButton
                        variant="image"
                        size="icon"
                        layout="horizontal"
                        onClick={onClickInstagramButtonHandle}
                        customStyles={s.instagramButton(isRoot)}
                    >
                        <Instagram />
                    </CommonButton>
                </div>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
